/**
 * useDesignDays Hook
 *
 * React hook for loading and managing design day data for a weather file.
 * Handles on-demand fetching, caching, and state management.
 */

import { useState, useEffect, useCallback } from 'react';
import { fetchDdyFromZip } from '../services/fetchDdy';
import { getCachedDdyData, cacheDdyData } from '../services/weatherFileCache';
import { getRecommendedDesignDays, groupDesignDaysByType } from '../services/ddyParser';
import type { ParsedDesignDay, DesignDayType, WeatherFileMetadata } from '../types';

export interface UseDesignDaysState {
  /** Parsed design days (null if not loaded) */
  designDays: ParsedDesignDay[] | null;
  /** Currently selected design day IDs */
  selectedIds: Set<string>;
  /** Recommended design day IDs */
  recommendedIds: string[];
  /** Design days grouped by type */
  groupedDesignDays: Record<DesignDayType, ParsedDesignDay[]> | null;
  /** Loading state */
  isLoading: boolean;
  /** Error message if loading failed */
  error: string | null;
  /** Whether DDY data is cached */
  isCached: boolean;
}

export interface UseDesignDaysActions {
  /** Load design days from ZIP archive */
  loadDesignDays: () => Promise<void>;
  /** Toggle selection of a design day */
  toggleSelection: (id: string) => void;
  /** Select multiple design days */
  selectMultiple: (ids: string[]) => void;
  /** Clear all selections */
  clearSelection: () => void;
  /** Select recommended design days */
  selectRecommended: () => void;
  /** Get selected design days */
  getSelectedDesignDays: () => ParsedDesignDay[];
  /** Reset state */
  reset: () => void;
}

export type UseDesignDaysReturn = UseDesignDaysState & UseDesignDaysActions;

/**
 * Hook for managing design day data loading and selection.
 */
export function useDesignDays(weatherFile: WeatherFileMetadata | null): UseDesignDaysReturn {
  const [designDays, setDesignDays] = useState<ParsedDesignDay[] | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [recommendedIds, setRecommendedIds] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isCached, setIsCached] = useState(false);

  // Reset state when weather file changes
  useEffect(() => {
    setDesignDays(null);
    setSelectedIds(new Set());
    setRecommendedIds([]);
    setError(null);
    setIsCached(false);
  }, [weatherFile?.id]);

  // Load design days
  const loadDesignDays = useCallback(async () => {
    if (!weatherFile) {
      setError('No weather file selected');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Check cache first
      const cachedData = await getCachedDdyData(weatherFile.id);
      if (cachedData) {
        const parsed = JSON.parse(cachedData) as ParsedDesignDay[];
        setDesignDays(parsed);
        setIsCached(true);

        // Set recommended design days
        const recommended = getRecommendedDesignDays(parsed);
        setRecommendedIds(recommended);

        // Pre-select recommended
        setSelectedIds(new Set(recommended));

        setIsLoading(false);
        return;
      }

      // Fetch from ZIP archive
      const parsed = await fetchDdyFromZip(weatherFile.urls.epw);
      setDesignDays(parsed);

      // Cache for future use
      await cacheDdyData(weatherFile.id, JSON.stringify(parsed));
      setIsCached(true);

      // Set recommended design days
      const recommended = getRecommendedDesignDays(parsed);
      setRecommendedIds(recommended);

      // Pre-select recommended
      setSelectedIds(new Set(recommended));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load design days';
      setError(message);
      setDesignDays(null);
    } finally {
      setIsLoading(false);
    }
  }, [weatherFile]);

  // Toggle selection
  const toggleSelection = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  // Select multiple
  const selectMultiple = useCallback((ids: string[]) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const id of ids) {
        next.add(id);
      }
      return next;
    });
  }, []);

  // Clear selection
  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  // Select recommended
  const selectRecommended = useCallback(() => {
    setSelectedIds(new Set(recommendedIds));
  }, [recommendedIds]);

  // Get selected design days
  const getSelectedDesignDays = useCallback((): ParsedDesignDay[] => {
    if (!designDays) return [];
    return designDays.filter((dd) => selectedIds.has(dd.id));
  }, [designDays, selectedIds]);

  // Reset state
  const reset = useCallback(() => {
    setDesignDays(null);
    setSelectedIds(new Set());
    setRecommendedIds([]);
    setError(null);
    setIsCached(false);
  }, []);

  // Compute grouped design days
  const groupedDesignDays = designDays ? groupDesignDaysByType(designDays) : null;

  return {
    designDays,
    selectedIds,
    recommendedIds,
    groupedDesignDays,
    isLoading,
    error,
    isCached,
    loadDesignDays,
    toggleSelection,
    selectMultiple,
    clearSelection,
    selectRecommended,
    getSelectedDesignDays,
    reset,
  };
}
